import { DSButton, DSFlex, DSSpacer, DSText } from '@zoe/ds-web'
import { useRouter } from 'next/router'
import React, { useContext, useEffect, useState } from 'react'
import { Image, ResponsiveImageType } from 'react-datocms'

import { FragmentComponent } from '@/types/graphql'

import { responsiveImageBlock } from '@/graphql/utils/commonBlocks'

import { TrackingContext } from '@/context/trackingContext'

import { BreakConfigProps, DSGrid, DSGridBlock } from '@/components/ds/Grid/Grid'

import { trackCtaClick } from '@/lib/gtm/gtmV2'

import { DSStyledStructuredText } from '../DesignSystem/DSStyledStructuredText'
import { processTypographyMarkdown } from '../DesignSystem/Typography'

export type MediaAndTextRecord = {
  __typename: 'MediaAndTextRecord'
  id: string
  eyebrow?: string
  title?: string
  subtitle?: string
  content?: {
    value: any
    blocks: Array<any>
  }
  ctaLabel?: string
  ctaUrl?: string
  image: {
    responsiveImage: ResponsiveImageType
  }
  imageDesktopPosition?: 'left' | 'right'
  dataTestId?: string
}

const fragment = `
  fragment MediaAndTextBlockFragment on MediaAndTextRecord {
    __typename
    id
    eyebrow
    title
    subtitle
    content {
      value
    }
    ctaLabel
    ctaUrl
    ctaLabelSecondary
    ctaUrlSecondary
    image {
      ${responsiveImageBlock()}
    }
    imageDesktopPosition
    dataTestId
    
  }
`

const TextBlock = ({ record }) => {
  const { section } = useContext(TrackingContext)
  const router = useRouter()

  const [hasPrimaryCta, setHasPrimaryCta] = useState(false)
  const [hasSecondaryCta, setHasSecondaryCta] = useState(false)

  const handleOnPress = (ctaLabel: string, ctaUrl: string) => {
    trackCtaClick(section || 'cms', ctaLabel || '')
    // If the URL contains an anchor, scroll to it
    if (ctaUrl?.includes('#')) {
      const anchor = ctaUrl.split('#')[1]
      const element = document.getElementById(anchor)
      if (element) {
        element.scrollIntoView({ behavior: 'smooth', block: 'start', inline: 'nearest' })
      }
    } else {
      router.push(ctaUrl || '')
    }
  }

  useEffect(() => {
    setHasPrimaryCta(!!(record?.ctaLabel && record?.ctaUrl))
    setHasSecondaryCta(!!(record?.ctaLabelSecondary && record?.ctaUrlSecondary))
  }, [record])

  return (
    <>
      <DSSpacer direction="vertical" size={144} breakAt={{ m: 72 }} />
      {record.eyebrow && (
        <>
          <DSText as="span" variant="fluid-eyebrow-100">
            {record.eyebrow}
          </DSText>
          <DSSpacer direction="vertical" size={12} />
        </>
      )}
      {record.title && (
        <>
          <DSText as="h1" variant="fluid-heading-900">
            {processTypographyMarkdown({ content: record.title, variant: 'fluid-heading-900' })}
          </DSText>
          <DSSpacer direction="vertical" size={16} />
        </>
      )}
      {record.subtitle && (
        <>
          <DSText as="h2" variant="fluid-heading-500" weight="light">
            {processTypographyMarkdown({ content: record.subtitle, variant: 'fluid-paragraph-400' })}
          </DSText>
          <DSSpacer direction="vertical" size={16} />
        </>
      )}
      {record.content && <DSStyledStructuredText data={record.content} />}
      {(hasPrimaryCta || hasSecondaryCta) && (
        <>
          <DSSpacer direction="vertical" size={40} />
          <DSFlex direction="column" alignItems="flex-start">
            <DSButton
              variant="primary"
              size="large"
              colorScheme="default"
              width="auto"
              onPress={() => handleOnPress(record?.ctaLabel || '', record?.ctaUrl || '')}
            >
              <DSSpacer direction="horizontal" size={32} />
              <DSText variant="fluid-paragraph-200" weight="semiBold">
                {record?.ctaLabel}
              </DSText>
              <DSSpacer direction="horizontal" size={32} />
            </DSButton>
            {hasSecondaryCta && (
              <>
                <DSSpacer direction="vertical" size={16} />
                <DSButton
                  variant="secondary"
                  size="large"
                  colorScheme="default"
                  width="auto"
                  onPress={() => handleOnPress(record?.ctaLabelSecondary || '', record?.ctaUrlSecondary || '')}
                >
                  <DSSpacer direction="horizontal" size={32} />
                  <DSText variant="fluid-paragraph-200" weight="semiBold">
                    {record?.ctaLabelSecondary}
                  </DSText>
                  <DSSpacer direction="horizontal" size={32} />
                </DSButton>
              </>
            )}
          </DSFlex>
        </>
      )}
      <DSSpacer direction="vertical" size={144} breakAt={{ m: 72 }} />
    </>
  )
}

export const MediaAndTextBlock: FragmentComponent<{}, MediaAndTextRecord> = ({ record }) => {
  let breakConfigText: BreakConfigProps = {
    s: { colSpan: 2 },
    m: { colSpan: 6 },
    l: { colSpan: 4, offset: 1, order: 1 },
    xl: { colSpan: 4, offset: 1, order: 1 },
  }

  let breakConfigMedia: BreakConfigProps = {
    s: { colSpan: 2 },
    m: { colSpan: 6 },
    l: { colSpan: 6 },
    xl: { colSpan: 6 },
  }

  if (record.imageDesktopPosition === 'right') {
    breakConfigText = {
      s: { colSpan: 2 },
      m: { colSpan: 6 },
      l: { colSpan: 4, offset: 1 },
      xl: { colSpan: 4, offset: 1 },
    }
    breakConfigMedia = {
      s: { colSpan: 2 },
      m: { colSpan: 6 },
      l: { colSpan: 6, offset: 1 },
      xl: { colSpan: 6, offset: 1 },
    }
  }

  return (
    <DSGrid data-testid={record?.dataTestId} fullWidth>
      <DSGridBlock breakConfig={breakConfigText} padded>
        <TextBlock record={record} />
      </DSGridBlock>
      <DSGridBlock breakConfig={breakConfigMedia}>
        <Image data={record.image?.responsiveImage} style={{ height: '100%' }} objectFit="cover" />
      </DSGridBlock>
    </DSGrid>
  )
}

MediaAndTextBlock.fragment = fragment
MediaAndTextBlock.recordName = 'MediaAndTextRecord'
